import { useCallback, useEffect, useMemo, useState } from "react";
import { Chip } from "@mui/material";
import {
  BellRing,
  CheckCheck,
  CheckCircle2,
  FileCheck2,
  XCircle,
} from "lucide-react";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import DataTable from "../../components/DataTable/DataTable";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import { getUserData } from "../../Actions/common";
import { fetchUserNotificationsAction } from "../../Actions/NotificationAction";
import { formatDateTime } from "../Common/DateFormat";
import CustomSnackbar from "../Common/snackBar";
import { useNotification } from "./NotificationProvider";
import "./NotificationsList.css";

const getNotificationMeta = (message = "") => {
  const normalized = String(message).toLowerCase();

  if (
    normalized.includes("rejected") ||
    normalized.includes("failed") ||
    normalized.includes("error")
  ) {
    return { tone: "danger", icon: XCircle, label: "Alert" };
  }

  if (
    normalized.includes("approved") ||
    normalized.includes("success") ||
    normalized.includes("updated")
  ) {
    return { tone: "success", icon: CheckCircle2, label: "Update" };
  }

  if (
    normalized.includes("request") ||
    normalized.includes("submitted") ||
    normalized.includes("created")
  ) {
    return { tone: "primary", icon: FileCheck2, label: "Request" };
  }

  return { tone: "neutral", icon: BellRing, label: "System" };
};

const NotificationsListSection = () => {
  const userData = localStorage.getItem("userData") ? getUserData() : null;
  const { notifications, setNotifications, markAllNotificationsRead } =
    useNotification();

  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
  const [filterValues, setFilterValues] = useState({
    search: "",
    status: "all",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const loadNotifications = useCallback(async () => {
    if (!userData?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetchUserNotificationsAction(userData.id, "all");
      const rows = response?.data?.data || response?.data || [];
      setNotifications(Array.isArray(rows) ? rows : []);
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Unable to load notification history",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [setNotifications, userData?.id]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const filteredRows = useMemo(() => {
    const search = filterValues.search?.trim().toLowerCase() || "";
    const status = filterValues.status || "all";

    return (notifications || [])
      .filter((item) => {
        const matchesStatus =
          status === "all" ||
          (status === "unread" && !item.isRead) ||
          (status === "read" && item.isRead);

        const matchesSearch =
          !search ||
          String(item.message || "")
            .toLowerCase()
            .includes(search);

        return matchesStatus && matchesSearch;
      })
      .sort(
        (a, b) =>
          new Date(b.createdAt || 0).getTime() -
          new Date(a.createdAt || 0).getTime()
      );
  }, [filterValues.search, filterValues.status, notifications]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [currentPage, filteredRows, pageSize]);

  const unreadCount = useMemo(
    () => (notifications || []).filter((item) => !item.isRead).length,
    [notifications]
  );

  const columns = [
    {
      field: "type",
      headerName: "Type",
      minWidth: 120,
      renderCell: ({ row }) => {
        const meta = getNotificationMeta(row.message);
        const Icon = meta.icon;
        return (
          <span className={`notif-type is-${meta.tone}`}>
            <Icon size={14} aria-hidden />
            {meta.label}
          </span>
        );
      },
    },
    {
      field: "message",
      headerName: "Notification",
      minWidth: 360,
      renderCell: ({ row }) => (
        <span className="notif-message">{row.message || "—"}</span>
      ),
    },
    {
      field: "isRead",
      headerName: "Status",
      minWidth: 120,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={row.isRead ? "Read" : "Unread"}
          className={`notif-status ${row.isRead ? "is-read" : "is-unread"}`}
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Received",
      minWidth: 180,
      renderCell: ({ row }) =>
        formatDateTime(row.createdAt, true),
    },
  ];

  const filters = [
    {
      name: "search",
      label: "Search",
      type: "search",
      placeholder: "Search notifications...",
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      defaultValue: "all",
      options: [
        { label: "All Status", value: "all" },
        { label: "Unread", value: "unread" },
        { label: "Read", value: "read" },
      ],
    },
  ];

  const handleFilterApply = (values) => {
    setFilterValues({
      search: values?.search || "",
      status: values?.status || "all",
    });
    setCurrentPage(1);
  };

  const handleFilterReset = (clearedValues) => {
    setFilterValues({
      search: "",
      status: "all",
      ...(clearedValues || {}),
    });
    setCurrentPage(1);
  };

  return (
    <div id="kt_content_container" className="notifications-page">
      <BreadcrumbsComponent />

      <DataTable
        toolbarActions={
          unreadCount > 0 ? (
            <button
              type="button"
              className="notifications-mark-all"
              onClick={markAllNotificationsRead}
            >
              <CheckCheck size={15} aria-hidden />
              Mark all as read
            </button>
          ) : null
        }
        columns={columns}
        rows={paginatedRows}
        loading={loading}
        totalCount={filteredRows.length}
        page={currentPage}
        pageSize={pageSize}
        filters={filters}
        filterValues={filterValues}
        getRowId={(row) => row.id}
        onFilterApply={handleFilterApply}
        onFilterReset={handleFilterReset}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default NotificationsListSection;
